import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, LockKey, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import PageShell from "@/components/PageShell";

export default function CreateTeam() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/teams/create", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/teams", { name, password });
      toast.success(`Team "${data.name}" created`);
      trackEvent("team_created", { team_id: data.team_id });
      navigate(`/teams/${data.team_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <PageShell
      eyebrow="Create team"
      title="Spin up a workspace."
      subtitle="Pick a name and a team password. Share both with your team — they join with just the Team ID + password. No signup needed on their end."
      narrow
    >
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
        <form onSubmit={submit} className="brut-card p-6 space-y-5" data-testid="create-team-form">
          <div>
            <div className="small-caps mb-2 flex items-center gap-1"><Users size={12}/> Team name</div>
            <input
              data-testid="team-name"
              className="input-brut w-full"
              required minLength={2} maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project Atlas"
            />
          </div>
          <div>
            <div className="small-caps mb-2 flex items-center gap-1"><LockKey size={12}/> Team password</div>
            <input
              data-testid="team-password"
              className="input-brut w-full" type="text" required minLength={4}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 4 characters"
            />
            <div className="text-[11px] text-black/55 mt-1 font-mono">
              Share this with team members along with the Team ID.
            </div>
          </div>
          <button
            data-testid="team-submit" type="submit" disabled={busy}
            className="btn-brut-primary w-full disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create team"} <ArrowRight size={16} weight="bold"/>
          </button>
        </form>

        <aside className="brut-card p-6 space-y-3">
          <div className="small-caps">What you get</div>
          <ul className="space-y-2 text-sm text-black/75">
            <li>· Auto-generated 8-character Team ID</li>
            <li>· 1 GB shared storage (Pro plans unlock more)</li>
            <li>· Up to 10 members</li>
            <li>· Shared file repository + activity log</li>
            <li>· You become the team leader</li>
          </ul>
          <div className="mt-5 small-caps">Already have an invite?</div>
          <Link to="/teams/join" className="btn-brut-accent w-full">
            Join a team <ArrowRight size={16}/>
          </Link>
        </aside>
      </div>
    </PageShell>
  );
}
